import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { UsersIcon, PlusIcon, PencilSimpleIcon, TrashIcon, ProhibitIcon, CheckCircleIcon, BroomIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import IpGeoChip from "@/components/IpGeoChip";
import RiskBadge from "@/components/RiskBadge";
import UserDetailsDialog from "@/components/UserDetailsDialog";
import AllIpsPanel from "@/components/AllIpsPanel";

const ACCOUNT_TYPES = [
  "retail_buyer",
  "retail_seller",
  "wholesale_buyer",
  "wholesale_seller",
];
const VERIFICATION_STATUSES = ["unsubmitted", "pending", "approved", "rejected"];
const ROLES = ["user", "admin"];

const BLANK_CREATE = {
  email: "",
  password: "",
  name: "",
  account_type: "retail_buyer",
  role: "user",
  verification_status: "approved",
};

export default function AdminUsers({ currentAdminId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | banned
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_CREATE);
  const [editTarget, setEditTarget] = useState(null); // user doc
  const [editForm, setEditForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  // Bulk-selection set for the row checkboxes — drives the bulk action
  // bar that appears above the list when any row is selected.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (filter === "banned") params.banned = true;
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, filter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reset the bulk selection whenever the filtered/search results change.
  useEffect(() => { setSelectedIds(new Set()); }, [q, filter]);

  const toggleSelect = (uid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };
  // Only seller-typed accounts can appear on the Beacon Spotlight,
  // so the "Select all" affordance limits itself to those rows.
  const visibleSellers = items.filter((u) => (u.account_type || "").endsWith("_seller"));
  const allSellersSelected =
    visibleSellers.length > 0 &&
    visibleSellers.every((u) => selectedIds.has(u.user_id));
  const toggleSelectAllSellers = () => {
    if (allSellersSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleSellers.map((u) => u.user_id)));
    }
  };
  const clearSelection = () => setSelectedIds(new Set());

  const submitBulkBeacon = async (allowed) => {
    if (!selectedIds.size) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/users/beacon-allowed/bulk", {
        user_ids: Array.from(selectedIds),
        allowed,
      });
      toast.success(
        `${allowed ? "Allowed" : "Disallowed"} ${r.data?.modified || 0} seller(s) on the Spotlight`
      );
      clearSelection();
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const submitCreate = async () => {
    try {
      await api.post("/admin/users", createForm);
      toast.success(`Created ${createForm.email}`);
      setCreateOpen(false);
      setCreateForm(BLANK_CREATE);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      email: u.email,
      name: u.name || "",
      username: u.username || "",
      account_type: u.account_type,
      role: u.role,
      verification_status: u.verification_status,
      featured_on_homepage: !!u.featured_on_homepage,
      is_founder: !!u.is_founder,
      beacon_allowed: !!u.beacon_allowed,
      password: "",
    });
  };

  const submitEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.patch(`/admin/users/${editTarget.user_id}`, payload);
      toast.success(`Updated ${editTarget.email}`);
      setEditTarget(null);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(`Deleted ${deleteTarget.email}`);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitBan = async () => {
    try {
      await api.post(`/admin/users/${banTarget.user_id}/ban`, {
        reason: banReason.trim() || null,
      });
      toast.success(`Banned ${banTarget.email}`);
      setBanTarget(null);
      setBanReason("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitUnban = async (u) => {  try {
      await api.post(`/admin/users/${u.user_id}/unban`);
      toast.success(`Unbanned ${u.email}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleCinemaGrant = async (u) => {
    const newVal = !u.cinema_grant;
    try {
      await api.post(`/admin/users/${u.user_id}/cinema-grant`, { grant: newVal });
      toast.success(
        newVal
          ? `🎬 Cinema mode granted to ${u.email}`
          : `Cinema mode revoked for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Auto-Detect & List — admin override for the yearly-Plus paywall. ON
  // mirrors a yearly subscription for entitlement purposes (the user can
  // run AI item detection in their own live shows without being charged).
  const toggleDetectGrant = async (u) => {
    const newVal = !u.detect_grant;
    try {
      await api.post(`/admin/users/${u.user_id}/detect-grant`, { grant: newVal });
      toast.success(
        newVal
          ? `🪄 Auto-Detect granted to ${u.email}`
          : `Auto-Detect revoked for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Cosmetic "sparkle avatar" override — toggles the rotating sparkle
  // ring + shimmer on this user's navbar avatar. No entitlements
  // attached, purely visual VIP treatment.
  const toggleAvatarSparkle = async (u) => {
    const newVal = !u.avatar_sparkle;
    try {
      await api.post(`/admin/users/${u.user_id}/avatar-sparkle-grant`, { grant: newVal });
      toast.success(
        newVal
          ? `✨ Sparkle avatar granted to ${u.email}`
          : `Sparkle avatar removed for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // TikTok-style blue verified checkmark. Independent from ID
  // verification — this is a curated "notable account" mark that
  // admins grant to reputable sellers, brands, partners, etc.
  const toggleVerifiedBadge = async (u) => {
    const newVal = !u.verified_badge;
    try {
      await api.post(`/admin/users/${u.user_id}/verified-badge`, { grant: newVal });
      toast.success(
        newVal
          ? `Verified checkmark granted to ${u.email}`
          : `Verified checkmark removed for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Admin override for the 500-sales escrow gate. When ON every future
  // sale releases instantly to the seller's connected payout method —
  // identical to the org-earned threshold but hand-granted.
  const toggleInstantPayout = async (u) => {
    const newVal = !u.instant_payout_enabled;
    try {
      await api.post(`/admin/users/${u.user_id}/instant-payout-grant`, { grant: newVal });
      toast.success(
        newVal
          ? `⚡ Instant payouts unlocked for ${u.email}`
          : `Instant payouts revoked for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const togglePlusGrant = async (u, days = 0) => {
    // Effective "is Plus" = paying subscriber OR admin-granted. We toggle
    // only the admin-grant flag here; paying subs are managed via Stripe.
    const isCurrentlyPlus = u.plus_status === "active";
    // If `days` is omitted (0) AND user already has Plus → revoke.
    // If `days` > 0 → always grant for that duration (extends current comp).
    const newVal = days > 0 ? true : !isCurrentlyPlus;
    if (!newVal && u.plus_admin_granted === false && u.plus_plan && u.plus_plan !== "admin_grant") {
      toast.error("This user is a paid subscriber — cancel via Stripe, not here.");
      return;
    }
    try {
      await api.post(`/admin/users/${u.user_id}/plus-grant`, {
        grant: newVal,
        days: newVal ? days : 0,
      });
      toast.success(
        newVal
          ? days > 0
            ? `👑 Plus comped to ${u.email} for ${days} days`
            : `👑 Plus granted to ${u.email} (permanent)`
          : `Plus revoked for ${u.email}`,
      );
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openCleanup = async () => {
    setCleanupBusy(true);
    setCleanupOpen(true);
    try {
      const { data } = await api.get("/admin/cleanup-test-data/preview");
      setCleanupPreview(data);
    } catch (e) {
      toast.error(apiError(e));
      setCleanupOpen(false);
    } finally {
      setCleanupBusy(false);
    }
  };

  const submitCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data");
      toast.success(`Deleted ${data.deleted} test account${data.deleted === 1 ? "" : "s"}`);
      setCleanupOpen(false);
      setCleanupPreview(null);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCleanupBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* IP audit · grouped — every IP touching every account, fraud lens. */}
      <AllIpsPanel
        onPickIp={(ip) => {
          // Setting `q` schedules a refresh via the useEffect on
          // [refresh] — no need to call any fetcher manually here.
          // (The previous `load(ip)` call was a leftover from a rename
          // and threw "load is not defined" at runtime.)
          setQ(ip);
          // Scroll the user list into view so the filtered results are visible.
          requestAnimationFrame(() => {
            const node = document.querySelector('[data-testid="admin-users-section"]');
            if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }}
      />

      <div
        className="border border-zinc-200 bg-white rounded-2xl overflow-hidden"
        data-testid="admin-users-section"
      >
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <UsersIcon size={22} className="text-[#1D78AB]" />
          <div>
            <div className="font-display font-bold">All users</div>
            <div className="text-xs text-zinc-500 font-mono">
              {total} total · create, edit, ban, or delete
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="inline-flex rounded-full border border-zinc-200 p-0.5 bg-zinc-50" data-testid="admin-users-filter-tabs">
            {[
              { id: "all", label: "All" },
              { id: "banned", label: "Banned" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setFilter(t.id)}
                className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full transition ${
                  filter === t.id
                    ? (t.id === "banned" ? "bg-red-600 text-white shadow" : "bg-[#1D78AB] text-white shadow")
                    : "text-zinc-600 hover:text-zinc-900"
                }`}
                data-testid={`admin-users-filter-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Input
            placeholder="Search email, name, or IP"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-64"
            data-testid="admin-users-search"
          />
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="admin-users-refresh-btn">
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={openCleanup}
            className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
            data-testid="admin-users-cleanup-btn"
          >
            <BroomIcon size={16} weight="bold" />
            Cleanup test data
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white gap-2"
            data-testid="admin-users-create-btn"
          >
            <PlusIcon size={16} weight="bold" />
            Create user
          </Button>
        </div>
      </div>

      {/* Bulk action bar — appears whenever any row is selected. Lets
          admins flip the Beacon Spotlight allowlist for many sellers at
          once (e.g. onboarding a new wave of Founder Sellers). */}
      {selectedIds.size > 0 && (
        <div
          className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between flex-wrap gap-3"
          data-testid="admin-users-bulk-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="font-bold text-amber-900">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={toggleSelectAllSellers}
              className="text-xs underline text-amber-800 hover:text-amber-900"
              data-testid="admin-users-bulk-select-all"
            >
              {allSellersSelected ? "Deselect all visible sellers" : `Select all ${visibleSellers.length} visible seller(s)`}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-zinc-500 hover:text-zinc-700"
              data-testid="admin-users-bulk-clear"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => submitBulkBeacon(true)}
              disabled={bulkBusy}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
              data-testid="admin-users-bulk-allow-beacon"
            >
              🌀 Allow on Spotlight
            </Button>
            <Button
              variant="outline"
              onClick={() => submitBulkBeacon(false)}
              disabled={bulkBusy}
              className="border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="admin-users-bulk-disallow-beacon"
            >
              Disallow
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="p-12 text-center text-zinc-500">
          {loading ? "Loading…" : q ? "No users match that search." : "No users yet."}
        </div>
      ) : (
        <div className="divide-y divide-zinc-200">
          {items.map((u) => {
            const isSelf = u.user_id === currentAdminId;
            const isSeller = (u.account_type || "").endsWith("_seller");
            return (
              <div
                key={u.user_id}
                className={`p-4 flex items-center gap-4 flex-wrap ${
                  selectedIds.has(u.user_id) ? "bg-amber-50/60" : ""
                }`}
                data-testid={`admin-user-row-${u.user_id}`}
              >
                {/* Bulk-select checkbox — only sellers can be Spotlight-eligible,
                    so we render the box for them only. Buyer rows show a spacer
                    to keep the layout aligned. */}
                {isSeller ? (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(u.user_id)}
                    onChange={() => toggleSelect(u.user_id)}
                    className="h-4 w-4 accent-amber-600 shrink-0"
                    data-testid={`admin-user-select-${u.user_id}`}
                    aria-label={`Select ${u.name || u.email}`}
                  />
                ) : (
                  <span className="w-4 shrink-0" aria-hidden="true" />
                )}
                <div className="h-10 w-10 bg-zinc-100 rounded-xl grid place-items-center font-semibold">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {u.name}
                    {u.role === "admin" && (
                      <span className="pill bg-red-600 text-white text-[10px]">ADMIN</span>
                    )}
                    {isSelf && (
                      <span className="pill bg-zinc-900 text-white text-[10px]">YOU</span>
                    )}
                    {u.is_founder && (
                      <span
                        className={`pill text-[10px] inline-flex items-center gap-1 ${
                          (u.account_type || "").includes("seller")
                            ? "bidora-founder-pill-seller"
                            : "bidora-founder-pill-buyer"
                        }`}
                        title="Founder member"
                        data-testid={`founder-pill-${u.user_id}`}
                      >
                        ★ FOUNDER
                      </span>
                    )}
                    {u.banned_at && (
                      <span
                        className="pill bg-red-700 text-white text-[10px] inline-flex items-center gap-1"
                        title={u.banned_reason || "No reason given"}
                        data-testid={`banned-pill-${u.user_id}`}
                      >
                        <ProhibitIcon size={10} weight="fill" />
                        BANNED
                      </span>
                    )}
                    {u.beacon_allowed && (
                      <span
                        className="pill bg-amber-300 text-amber-950 text-[10px] inline-flex items-center gap-1 font-black"
                        title="Eligible for the homepage Beacon Spotlight"
                        data-testid={`beacon-pill-${u.user_id}`}
                      >
                        🌀 SPOTLIGHT
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                  {(u.signup_ip || u.verification_ip || u.last_seen_ip) && (
                    <div
                      className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5"
                      data-testid={`admin-user-ips-${u.user_id}`}
                    >
                      {u.signup_ip && (
                        <IpGeoChip
                          label="signup:"
                          ip={u.signup_ip}
                          geo={u.signup_ip_geo}
                          testid={`admin-user-signup-ip-${u.user_id}`}
                        />
                      )}
                      {u.verification_ip && u.verification_ip !== u.signup_ip && (
                        <IpGeoChip
                          label="id:"
                          ip={u.verification_ip}
                          geo={u.verification_ip_geo}
                          testid={`admin-user-verif-ip-${u.user_id}`}
                        />
                      )}
                      {u.last_seen_ip &&
                        u.last_seen_ip !== u.signup_ip &&
                        u.last_seen_ip !== u.verification_ip && (
                          <IpGeoChip
                            label="last:"
                            ip={u.last_seen_ip}
                            geo={u.last_seen_ip_geo}
                            testid={`admin-user-last-ip-${u.user_id}`}
                          />
                        )}
                    </div>
                  )}
                  {u.banned_at && u.banned_reason && (
                    <div className="text-[11px] text-red-600 mt-0.5 italic">
                      Reason: {u.banned_reason}
                    </div>
                  )}
                </div>
                <RiskBadge risk={u.risk} testid={`admin-user-risk-${u.user_id}`} />
                <span
                  className={`pill text-white ${
                    u.segment === "wholesale" ? "bg-amber-500" : "bg-[#1D78AB]"
                  }`}
                >
                  {u.segment}
                </span>
                <span className="text-xs text-zinc-500 hidden md:inline">
                  {u.account_type.replace("_", " ")}
                </span>
                <span
                  className={`pill text-[10px] ${
                    u.verification_status === "approved"
                      ? "bg-emerald-600 text-white"
                      : u.verification_status === "pending"
                      ? "bg-amber-500 text-white"
                      : u.verification_status === "rejected"
                      ? "bg-red-600 text-white"
                      : "bg-zinc-300 text-zinc-700"
                  }`}
                >
                  {u.verification_status}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDetailsTarget(u)}
                  className="border-[#1D78AB]/30 text-[#1D78AB] hover:bg-[#1D78AB]/5"
                  data-testid={`admin-user-details-${u.user_id}`}
                  title="See IPs, GeoIP, risk signals, full audit"
                >
                  <MagnifyingGlassIcon size={14} className="mr-1" />
                  Details
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(u)}
                  data-testid={`admin-user-edit-${u.user_id}`}
                >
                  <PencilSimpleIcon size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCinemaGrant(u)}
                  className={
                    u.cinema_grant
                      ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  }
                  data-testid={`admin-user-cinema-toggle-${u.user_id}`}
                  data-cinema-grant={u.cinema_grant ? "1" : "0"}
                  title={u.cinema_grant
                    ? "Cinema mode is GRANTED — click to revoke."
                    : "Grant Cinema mode (4K HLS) without Plus subscription."}
                >
                  🎬 {u.cinema_grant ? "Cinema · on" : "Cinema"}
                </Button>
                {/* Auto-Detect grant — sells separately from Cinema even
                    though both ship on Plus. We expose a distinct toggle
                    so we can comp Detect to alpha testers without also
                    giving them Mux 4K (which costs us per-minute).  */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleDetectGrant(u)}
                  className={
                    u.detect_grant
                      ? "border-[#1D78AB] bg-[#1D78AB]/10 text-[#1D78AB] hover:bg-[#1D78AB]/15"
                      : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  }
                  data-testid={`admin-user-detect-toggle-${u.user_id}`}
                  data-detect-grant={u.detect_grant ? "1" : "0"}
                  title={u.detect_grant
                    ? "Auto-Detect is GRANTED — click to revoke."
                    : "Grant Auto-Detect & List access without yearly Plus."}
                >
                  🪄 {u.detect_grant ? "Detect · on" : "Detect"}
                </Button>
                {/* Sparkle Avatar — purely cosmetic VIP toggle. Lights up
                    the rotating ring + shimmer on this user's navbar
                    avatar. role=admin gets it automatically. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAvatarSparkle(u)}
                  className={
                    u.avatar_sparkle
                      ? "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  }
                  data-testid={`admin-user-sparkle-toggle-${u.user_id}`}
                  data-sparkle={u.avatar_sparkle ? "1" : "0"}
                  title={u.avatar_sparkle
                    ? "Sparkle avatar is ON — click to remove the rotating ring + shimmer."
                    : "Light up this user's navbar avatar with a rotating sparkle ring + shimmer."}
                >
                  ✨ {u.avatar_sparkle ? "Sparkle · on" : "Sparkle"}
                </Button>
                {/* Verified checkmark — TikTok-style blue badge shown next
                    to the user's display name across the platform. Purely
                    curatorial; independent from the ID-verification queue. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleVerifiedBadge(u)}
                  className={
                    u.verified_badge
                      ? "border-sky-400 bg-sky-50 text-sky-700 hover:bg-sky-100"
                      : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  }
                  data-testid={`admin-user-verified-toggle-${u.user_id}`}
                  data-verified={u.verified_badge ? "1" : "0"}
                  title={u.verified_badge
                    ? "Verified checkmark is ON — click to revoke."
                    : "Grant this user the blue verified checkmark next to their display name."}
                >
                  {u.verified_badge ? "✓ Verified · on" : "Verify"}
                </Button>
                {/* Instant Payout toggle — admin override for the 500-sales
                    gate. Shows status of the bypass: "Instant · on" when the
                    seller is currently bypassing escrow (either admin-granted
                    OR organically crossed 500 sales). Tooltip explains why
                    it's currently locked when the user hit it organically. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleInstantPayout(u)}
                  className={
                    u.instant_payout_enabled
                      ? "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  }
                  data-testid={`admin-user-instant-payout-toggle-${u.user_id}`}
                  data-instant-payout={u.instant_payout_enabled ? "1" : "0"}
                  title={
                    u.instant_payout_enabled
                      ? (u.instant_payout_admin_granted
                          ? "Instant payouts ADMIN-GRANTED — click to revoke."
                          : `Instant payouts EARNED (${u.total_sales_count || 0} sales). Click to admin-revoke.`)
                      : `Grant instant payouts (bypasses the 500-sales gate). Currently at ${u.total_sales_count || 0}/500.`
                  }
                >
                  ⚡ {u.instant_payout_enabled
                    ? (u.instant_payout_admin_granted ? "Instant · admin" : "Instant · earned")
                    : "Instant"}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={
                        u.plus_status === "active"
                          ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      }
                      data-testid={`admin-user-plus-toggle-${u.user_id}`}
                      data-plus-active={u.plus_status === "active" ? "1" : "0"}
                      title={
                        u.plus_status === "active"
                          ? (u.plus_admin_granted
                              ? `Plus admin-granted${u.plus_until ? ` until ${new Date(u.plus_until).toLocaleDateString()}` : " (permanent)"}`
                              : `Paid ${u.plus_plan || "Plus"} subscriber`)
                          : "Comp Bidorah Plus for a chosen duration"
                      }
                    >
                      👑 {u.plus_status === "active"
                        ? (u.plus_admin_granted
                            ? (u.plus_until
                                ? `Plus · ${Math.max(0, Math.ceil((new Date(u.plus_until) - new Date()) / 86400000))}d`
                                : "Plus · perm")
                            : "Plus · paid")
                        : "Plus ▾"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 bg-white border-zinc-200 text-zinc-900"
                    data-testid={`admin-user-plus-menu-${u.user_id}`}
                  >
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-zinc-500">
                      {u.plus_status === "active"
                        ? (u.plus_admin_granted
                            ? "Extend or revoke comp"
                            : "Paid subscription (read-only)")
                        : "Comp Bidorah Plus"}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {/* Block changes on PAID subscribers — they manage via Stripe. */}
                    {u.plus_status === "active" && !u.plus_admin_granted && u.plus_plan !== "admin_grant" ? (
                      <DropdownMenuItem disabled className="text-xs text-zinc-500">
                        Paid {u.plus_plan} — cancel via Stripe.
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem
                          onClick={() => togglePlusGrant(u, 7)}
                          data-testid={`admin-plus-comp-7-${u.user_id}`}
                        >
                          Comp for 7 days
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => togglePlusGrant(u, 30)}
                          data-testid={`admin-plus-comp-30-${u.user_id}`}
                        >
                          Comp for 30 days
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => togglePlusGrant(u, 90)}
                          data-testid={`admin-plus-comp-90-${u.user_id}`}
                        >
                          Comp for 90 days
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => togglePlusGrant(u, 365)}
                          data-testid={`admin-plus-comp-365-${u.user_id}`}
                        >
                          Comp for 1 year
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => togglePlusGrant(u, 0)}
                          className="font-semibold"
                          data-testid={`admin-plus-comp-permanent-${u.user_id}`}
                        >
                          {u.plus_status === "active" ? "Revoke Plus" : "Grant permanently"}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {u.banned_at ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitUnban(u)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`admin-user-unban-${u.user_id}`}
                  >
                    <CheckCircleIcon size={14} className="mr-1" />
                    Unban
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setBanTarget(u); setBanReason(""); }}
                    disabled={isSelf}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                    data-testid={`admin-user-ban-${u.user_id}`}
                  >
                    <ProhibitIcon size={14} className="mr-1" />
                    Ban
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteTarget(u)}
                  disabled={isSelf}
                  className="border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
                  data-testid={`admin-user-delete-${u.user_id}`}
                >
                  <TrashIcon size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* User details dialog — IPs, GeoIP, risk audit, all admin-only fields. */}
      <UserDetailsDialog
        user={detailsTarget}
        onClose={() => setDetailsTarget(null)}
      />

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="admin-users-create-dialog">
          <DialogHeader>
            <DialogTitle>Create a new user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Email" value={createForm.email}
              onChange={(v) => setCreateForm((f) => ({ ...f, email: v }))}
              testid="admin-users-create-email" />
            <LabeledInput label="Name" value={createForm.name}
              onChange={(v) => setCreateForm((f) => ({ ...f, name: v }))}
              testid="admin-users-create-name" />
            <LabeledInput label="Password (min 6 chars)" type="password" value={createForm.password}
              onChange={(v) => setCreateForm((f) => ({ ...f, password: v }))}
              testid="admin-users-create-password" />
            <LabeledSelect label="Account type" value={createForm.account_type}
              onChange={(v) => setCreateForm((f) => ({ ...f, account_type: v }))}
              options={ACCOUNT_TYPES} testid="admin-users-create-accounttype" />
            <LabeledSelect label="Role" value={createForm.role}
              onChange={(v) => setCreateForm((f) => ({ ...f, role: v }))}
              options={ROLES} testid="admin-users-create-role" />
            <LabeledSelect label="Verification status" value={createForm.verification_status}
              onChange={(v) => setCreateForm((f) => ({ ...f, verification_status: v }))}
              options={VERIFICATION_STATUSES} testid="admin-users-create-verification" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={submitCreate}
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
              data-testid="admin-users-create-submit"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent data-testid="admin-users-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editTarget?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Email" value={editForm.email || ""}
              onChange={(v) => setEditForm((f) => ({ ...f, email: v }))}
              testid="admin-users-edit-email" />
            {/* Full Name — internal only, used by admins for ID matching
                and KYC review. NEVER rendered in any public UI; the only
                public-facing identifier is `username`. */}
            <div>
              <LabeledInput label="Full Name (internal · admin only)" value={editForm.name || ""}
                onChange={(v) => setEditForm((f) => ({ ...f, name: v }))}
                testid="admin-users-edit-name" />
              <p className="mt-1 text-[11px] text-zinc-500 leading-snug">
                Stored privately for ID verification &amp; admin review.
                <strong className="text-zinc-700"> Never shown publicly.</strong>{" "}
                Only the @username is visible to other users.
              </p>
            </div>
            <LabeledInput label="Username (public @handle)" value={editForm.username || ""}
              onChange={(v) => setEditForm((f) => ({ ...f, username: v.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
              testid="admin-users-edit-username" />
            <LabeledInput
              label="New password (leave blank to keep current)"
              type="password"
              value={editForm.password || ""}
              onChange={(v) => setEditForm((f) => ({ ...f, password: v }))}
              testid="admin-users-edit-password"
            />
            <LabeledSelect label="Account type" value={editForm.account_type || "retail_buyer"}
              onChange={(v) => setEditForm((f) => ({ ...f, account_type: v }))}
              options={ACCOUNT_TYPES} testid="admin-users-edit-accounttype" />
            <LabeledSelect label="Role" value={editForm.role || "user"}
              onChange={(v) => setEditForm((f) => ({ ...f, role: v }))}
              options={ROLES} testid="admin-users-edit-role" />
            <LabeledSelect label="Verification status" value={editForm.verification_status || "unsubmitted"}
              onChange={(v) => setEditForm((f) => ({ ...f, verification_status: v }))}
              options={VERIFICATION_STATUSES} testid="admin-users-edit-verification" />
            <label
              className="flex items-start gap-3 px-3 py-2 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer"
              data-testid="admin-users-edit-featured-row"
            >
              <input
                type="checkbox"
                checked={!!editForm.featured_on_homepage}
                onChange={(e) => setEditForm((f) => ({ ...f, featured_on_homepage: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-[#1D78AB]"
                data-testid="admin-users-edit-featured"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold">Show on public homepage strips</div>
                <div className="text-xs text-zinc-500 leading-snug mt-0.5">
                  Admin/moderator accounts are hidden from public Recently-Verified strips
                  by default. Tick this to feature this account anyway (e.g. your own
                  flagship seller account).
                </div>
              </div>
            </label>
            <label
              className="flex items-start gap-3 px-3 py-2 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-pink-50 hover:from-amber-100 hover:to-pink-100 cursor-pointer"
              data-testid="admin-users-edit-founder-row"
            >
              <input
                type="checkbox"
                checked={!!editForm.is_founder}
                onChange={(e) => setEditForm((f) => ({ ...f, is_founder: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-[#FF7AC6]"
                data-testid="admin-users-edit-founder"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span>★ Grant Founder Member status</span>
                </div>
                <div className="text-xs text-zinc-600 leading-snug mt-0.5">
                  Unlocks 2% cashback wallet, 30s live-stream head-start, and the
                  ★ Founder pill across the app. Sellers get a pink-purple
                  gradient pill; buyers get a blue-white pill.
                </div>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 px-3 py-2 rounded-lg border cursor-pointer ${
                editForm.beacon_allowed
                  ? "border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100"
                  : "border-zinc-200 bg-white hover:bg-zinc-50"
              }`}
              data-testid="admin-users-edit-beacon-row"
            >
              <input
                type="checkbox"
                checked={!!editForm.beacon_allowed}
                onChange={(e) => setEditForm((f) => ({ ...f, beacon_allowed: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-amber-600"
                data-testid="admin-users-edit-beacon-allowed"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span>🌀 Allow on Beacon Spotlight</span>
                </div>
                <div className="text-xs text-zinc-600 leading-snug mt-0.5">
                  <b>OFF by default.</b> Tick this to opt the seller into the
                  homepage Beacon visuals (Spotlight wall, Coliseum Ribbon,
                  Headliner Stage, Pulse Ring, Velocity Lane) and make them
                  eligible to earn weekly trophies. Existing trophies stay
                  on their profile regardless of this toggle.
                </div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              onClick={submitEdit}
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
              data-testid="admin-users-edit-submit"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup test data dialog */}
      <Dialog open={cleanupOpen} onOpenChange={(v) => { if (!v) { setCleanupOpen(false); setCleanupPreview(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="cleanup-test-dialog">
          <DialogHeader>
            <DialogTitle>Delete test accounts?</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {cleanupBusy && !cleanupPreview ? (
              <p className="text-sm text-zinc-600">Scanning for test accounts…</p>
            ) : cleanupPreview ? (
              <>
                <p className="text-sm text-zinc-700">
                  Found{" "}
                  <span className="font-bold text-amber-700">
                    {cleanupPreview.count} account{cleanupPreview.count === 1 ? "" : "s"}
                  </span>{" "}
                  matching test patterns ({" "}
                  <span className="font-mono text-[11px] text-zinc-500">
                    e2e_, ip_test_, geo_test_, ban_test_, sender_fix_, verify_check_, webhook_check_, replay_test_, @mailinator.com, @example.com, @test.com…
                  </span>
                  ). Admin accounts are protected.
                </p>
                {cleanupPreview.count > 0 && (
                  <div className="max-h-72 overflow-auto border border-zinc-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-1.5">Email</th>
                          <th className="px-3 py-1.5">Name</th>
                          <th className="px-3 py-1.5">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cleanupPreview.items.map((u) => (
                          <tr key={u.user_id} className="border-t border-zinc-100">
                            <td className="px-3 py-1.5 font-mono">{u.email}</td>
                            <td className="px-3 py-1.5">{u.name}</td>
                            <td className="px-3 py-1.5 text-zinc-500">{u.account_type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCleanupOpen(false); setCleanupPreview(null); }}>Cancel</Button>
            <Button
              onClick={submitCleanup}
              disabled={cleanupBusy || !cleanupPreview || cleanupPreview.count === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cleanup-test-submit"
            >
              <TrashIcon size={14} className="mr-1.5" />
              {cleanupBusy ? "Deleting…" : `Delete ${cleanupPreview?.count ?? 0} account${cleanupPreview?.count === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban dialog */}
      <Dialog open={!!banTarget} onOpenChange={(v) => { if (!v) { setBanTarget(null); setBanReason(""); } }}>
        <DialogContent data-testid="admin-users-ban-dialog">
          <DialogHeader>
            <DialogTitle>Ban this user?</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <p>
              You are about to ban{" "}
              <span className="font-bold">{banTarget?.email}</span>. They will be
              kicked out of any active session and won&apos;t be able to log in,
              bid, or check out. Their data stays for audit; you can lift the ban
              anytime with <b>Unban</b>.
            </p>
            <LabeledInput
              label="Reason (optional, shown to the user on next login attempt)"
              value={banReason}
              onChange={setBanReason}
              testid="admin-users-ban-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBanTarget(null); setBanReason(""); }}>Cancel</Button>
            <Button
              onClick={submitBan}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-users-ban-submit"
            >
              <ProhibitIcon size={14} className="mr-1.5" weight="fill" />
              Ban user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="admin-users-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this user?</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm">
            <p>
              You are about to permanently delete{" "}
              <span className="font-bold">{deleteTarget?.email}</span>. This does not
              remove their historical listings, orders, or payouts — those stay for
              audit. Their account row will be gone and they won&apos;t be able to log in.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={submitDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-users-delete-submit"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
        {label}
      </span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
        data-testid={testid}
      />
    </label>
  );
}

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
        {label}
      </span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
